import { useCallback, useMemo, useState, useEffect } from "react";
import {
  Root,
  Portal,
  Overlay,
  Content,
  Drawer,
} from "vaul";

import { interpolate } from "@utils";
import { useKinndomContext } from "@contexts/KinndomContext";
import CloseButton from "@elements/CloseButton";

import { buildFavoritedPagesMenuItems } from "@utils/favoritedPages";
import DrawerMenu from "@components/navbar/drawer/DrawerMenu";
import UserProfileDrawerItem from "@components/navbar/drawer/UserProfileDrawerItem";
import UserLogoutDrawerItem from "@components/navbar/drawer/UserLogoutDrawerItem";

export default function NavbarDrawer(props) {
  const {
    isExpanded,
    setIsExpanded,
  } = props;

  const kinndom = useKinndomContext();

  const {
    kinndomKey,
    defaultWebsiteId,
    defaultWebsiteBaseUrl,
    isAuthenticated,
    isCurrentUserAdmin,
    currentUserAvatarUrl,
    currentUserName,
  } = window.appContext;

  /**************************************************************************************************/

  const [expandedMenus, setExpandedMenus] = useState({});

  const toggleMenu = (index) => {
    setExpandedMenus(prev => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const closeDrawer = useCallback(() => {
    setIsExpanded(false);
  }, [setIsExpanded]);

  const interpolationData = useMemo(() => ({
    kinndomKey: kinndomKey,
    defaultWebsiteId: defaultWebsiteId,
    defaultWebsiteBaseUrl: defaultWebsiteBaseUrl,

    kinndom: kinndom,
    config: kinndom.configuration,
  }), [kinndomKey, defaultWebsiteId, defaultWebsiteBaseUrl, kinndom]);

  const favoritedPages = window.FAVORITED_PAGES || [];

  const navbarMenus = useMemo(() => {
    return (window.NAVBAR_MENUS || [])
      .filter((menu) => (
        !menu.isHidden
        && (!menu.isAdminOnly || isCurrentUserAdmin)
      )).map((menu) => interpolate(menu, interpolationData));
  }, [isCurrentUserAdmin, interpolationData]);

  const actionsNavbarMenu = navbarMenus.at(-1);
  const standardNavbarMenus = navbarMenus.slice(0, -1);

  const drawerMenus = useMemo(() => {
    const menus = standardNavbarMenus.map((menu, index) => ({
      key: `menu-${index}`,
      label: menu.label,
      items: menu.items,
    }));

    const favoritesMenuItems = buildFavoritedPagesMenuItems({ favoritedPages });

    if (favoritesMenuItems.length > 0) {
      menus.push({
        key: "favorites-menu",
        label: {
          text: "Favorites",
          icon: "star",
        },
        items: favoritesMenuItems,
      });
    }

    if (actionsNavbarMenu) {
      menus.push({
        key: "actions-menu",
        label: actionsNavbarMenu.label,
        items: actionsNavbarMenu.items,
      });
    }

    return menus;
  }, [standardNavbarMenus, favoritedPages, actionsNavbarMenu]);

  useEffect(() => {
    if (!isExpanded) return;

    const initialState = {};

    drawerMenus.forEach((_, index) => {
      initialState[index] = false;
    });

    setExpandedMenus(initialState);
  }, [isExpanded, drawerMenus]);

  /**************************************************************************************************/

  if (!isAuthenticated) return null;

  return (
    <Root
      direction="left"
      open={isExpanded}
      onOpenChange={setIsExpanded}
    >
      <Portal>
        <Overlay className="fixed inset-0 bg-black/70 z-900" />

        <Content className="z-1000 bg-(--navbar-background-color) flex flex-col h-full w-full fixed inset-y-0 left-0 outline-none overflow-y-auto">
          <div className="p-4 flex items-center justify-between border-b border-b-[#66666633] bg-black/30 sticky top-0 z-10">
            <div className="text-lg font-medium text-(--navbar-foreground-color) px-2 font-mono">
              {kinndomKey}
            </div>

            <Drawer.Close asChild>
              <CloseButton className="text-(--navbar-foreground-color) border-none bg-transparent hover:bg-black/20 w-8 h-8 text-xl" />
            </Drawer.Close>
          </div>

          <div className="sr-only">
            <Drawer.Title>
              Navigation Menu
            </Drawer.Title>
            <Drawer.Description>
              Access site sections and features
            </Drawer.Description>
          </div>

          <div className="flex flex-col overflow-y-auto flex-1">
            {drawerMenus.map((menu, index) => (
              <DrawerMenu
                key={menu.key}
                label={menu.label}
                items={menu.items}
                isExpanded={expandedMenus[index]}
                closeDrawer={closeDrawer}
                onClick={() => toggleMenu(index)}
              />
            ))}
          </div>

          <div className="border-t border-t-[#66666633] bg-black/30 sticky bottom-0">
            <UserProfileDrawerItem
              userName={currentUserName}
              userAvatarUrl={currentUserAvatarUrl}
              closeDrawer={closeDrawer}
            />

            <UserLogoutDrawerItem
              closeDrawer={closeDrawer}
            />
          </div>
        </Content>
      </Portal>
    </Root>
  );
}
